/**
 * /security/admins — admin user management page.
 *
 * Lists every admin with a THREE-state status — Pending (invited, never
 * logged in), Active, Inactive (deactivated) — and state-dependent
 * per-row actions:
 *   - Edit roles + display_name (gated on admin.update + admin.role.assign)
 *   - Deactivate (Active only; gated on admin.deactivate; disabled for
 *     self + last super_admin, with tooltips explaining why)
 *   - Reactivate (Inactive only; gated on admin.deactivate)
 *   - Resend invite (Pending only; gated on admin.create)
 *
 * Toolbar action:
 *   - Invite admin (gated on admin.create) → opens AdminInviteModal.
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-rbac-design.md §6.6.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { UserPlus, UserX, UserCheck, Edit, AlertTriangle, Send } from 'lucide-react';
import { adminFetch } from '@/providers/admin-api';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import { toast } from '@/hooks/useToast';
import { AdminInviteModal } from './AdminInviteModal';
import { AdminEditModal } from './AdminEditModal';

interface AdminRow {
  id: string;
  email: string;
  display_name: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  role_keys: string[];
  primary_role_key: string;
  /**
   * Real Cognito account status ('CONFIRMED', 'FORCE_CHANGE_PASSWORD', …) or
   * null when the backend's Cognito lookup failed. Drives the accurate
   * Pending/Active badge — last_login_at alone only tracks panel logins.
   */
  cognito_status?: string | null;
}

export function AdminsListPage() {
  const apiUrl = useApiUrl();
  const { hasPermission, admin: me } = useAdminPermissions();
  const [admins, setAdmins] = useState<AdminRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminRow | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<AdminRow | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Per-row in-flight id for the direct (no-confirm-dialog) actions —
  // Reactivate + Resend invite — so only the clicked row's button shows
  // a busy state.
  const [rowBusyId, setRowBusyId] = useState<string | null>(null);

  const canInvite = hasPermission('admin.create');
  const canEdit = hasPermission('admin.update');
  const canEditRoles = hasPermission('admin.role.assign');
  const canDeactivate = hasPermission('admin.deactivate');

  const reload = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await adminFetch(`${apiUrl}/v1/admin/admins`);
      const data: AdminRow[] = await res.json();
      setAdmins(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load admins');
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Last-active-super-admin count for the self-protection guard.
  // Mirrors the backend check; we surface it as a disabled state on
  // the Deactivate button (cheaper UX than letting the server 409).
  const activeSuperCount = useMemo(
    () =>
      admins.filter(
        a => a.is_active && a.role_keys.includes('super_admin'),
      ).length,
    [admins],
  );

  const isLastActiveSuper = (a: AdminRow) =>
    a.is_active &&
    a.role_keys.includes('super_admin') &&
    activeSuperCount <= 1;

  const onDeactivate = async () => {
    if (!deactivateTarget) return;
    setSubmitting(true);
    try {
      await adminFetch(
        `${apiUrl}/v1/admin/admins/${deactivateTarget.id}`,
        { method: 'DELETE' },
      );
      toast({
        title: 'Admin deactivated',
        description: `${deactivateTarget.email} no longer has admin access.`,
      });
      setDeactivateTarget(null);
      await reload();
    } catch (e: any) {
      toast({
        title: 'Deactivation failed',
        description: e?.message || 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const onReactivate = async (a: AdminRow) => {
    setRowBusyId(a.id);
    try {
      await adminFetch(
        `${apiUrl}/v1/admin/admins/${a.id}/reactivate`,
        { method: 'POST' },
      );
      toast({
        title: 'Admin reactivated',
        description: `${a.email} can sign in again.`,
      });
      await reload();
    } catch (e: any) {
      toast({
        title: 'Reactivation failed',
        description: e?.message || 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setRowBusyId(null);
    }
  };

  const onResendInvite = async (a: AdminRow) => {
    setRowBusyId(a.id);
    try {
      const res = await adminFetch(
        `${apiUrl}/v1/admin/admins/${a.id}/resend-invite`,
        { method: 'POST' },
      );
      let emailSent = true;
      try {
        const body = await res.json();
        if (body && typeof body === 'object' && 'email_sent' in body) {
          emailSent = (body as { email_sent?: boolean }).email_sent !== false;
        }
      } catch {
        // Non-JSON success body — assume the invite went out.
      }
      if (emailSent) {
        toast({
          title: 'Invite resent',
          description: `A fresh invite was sent to ${a.email}.`,
        });
      } else {
        toast({
          title: 'Invite resent but email failed to send',
          description: `A fresh temporary password was set for ${a.email}, but the invite email could not be delivered.`,
          variant: 'destructive',
        });
      }
      await reload();
    } catch (e: any) {
      // Errors come back as RFC7807 problem+json — the code/reason is in body.title.
      const reason = String(e?.body?.title ?? e?.body?.message ?? e?.message ?? '');
      let title = 'Resend failed';
      let description = reason || 'Unknown error';
      if (/already_active/i.test(reason)) {
        title = 'Nothing to resend';
        description = `${a.email} has already set up their account, so there's no pending invite to resend.`;
      } else if (/inactive/i.test(reason)) {
        title = 'Admin is deactivated';
        description = `${a.email} is inactive. Reactivate them first, then resend the invite.`;
      }
      toast({ title, description, variant: 'destructive' });
    } finally {
      setRowBusyId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Admin users</h2>
          <p className="text-muted-foreground">
            {admins.length} total &middot; {activeSuperCount} active Super Admin
            {activeSuperCount === 1 ? '' : 's'}
          </p>
        </div>
        {canInvite && (
          <button
            onClick={() => setShowInvite(true)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <UserPlus className="h-4 w-4" />
            Invite User
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50 text-left text-sm">
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Roles</th>
                <th className="px-4 py-3 font-medium">Last login</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && admins.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}
              {!isLoading && admins.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    No admins yet. Invite one to get started.
                  </td>
                </tr>
              )}
              {admins.map(a => {
                const isSelf = a.id === me?.id;
                const lastSuper = isLastActiveSuper(a);
                // Three-state lifecycle, derived from BOTH the real Cognito
                // account status and last_login_at (which only tracks
                // admin-PANEL logins):
                //   - deactivated                       → Inactive
                //   - Cognito FORCE_CHANGE_PASSWORD     → Pending (never
                //     accepted the invite) — show Resend
                //   - Cognito CONFIRMED OR has panel-login → Active
                //   - Cognito status unknown (lookup failed) → fall back to
                //     the old last_login_at heuristic so it degrades gracefully.
                const lifecycle: 'inactive' | 'pending' | 'active' = !a.is_active
                  ? 'inactive'
                  : a.cognito_status === 'FORCE_CHANGE_PASSWORD'
                    ? 'pending'
                    : a.cognito_status === 'CONFIRMED' || a.last_login_at
                      ? 'active'
                      : !a.last_login_at
                        ? 'pending'
                        : 'active';
                const rowBusy = rowBusyId === a.id;
                const deactivateDisabled = !canDeactivate || isSelf || lastSuper || !a.is_active;
                const deactivateReason = isSelf
                  ? 'You cannot deactivate yourself'
                  : lastSuper
                    ? 'Cannot deactivate the last active Super Admin'
                    : !a.is_active
                      ? 'Already inactive'
                      : '';

                return (
                  <tr key={a.id} className="border-b last:border-0">
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium">{a.email}</div>
                      {isSelf && (
                        <div className="text-xs text-muted-foreground">you</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">{a.display_name}</td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex flex-wrap gap-1">
                        {a.role_keys.length === 0 ? (
                          <span className="text-xs text-muted-foreground italic">
                            no roles
                          </span>
                        ) : (
                          a.role_keys.map(k => (
                            <span
                              key={k}
                              className={
                                k === 'super_admin'
                                  ? 'inline-flex rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary'
                                  : 'inline-flex rounded-full bg-muted px-2 py-0.5 text-xs font-medium'
                              }
                            >
                              {k}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {a.last_login_at
                        ? new Date(a.last_login_at).toLocaleString()
                        : lifecycle === 'pending'
                          ? `Invited ${new Date(a.created_at).toLocaleString()}`
                          : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {lifecycle === 'active' && (
                        <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                          Active
                        </span>
                      )}
                      {lifecycle === 'pending' && (
                        <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                          Pending
                        </span>
                      )}
                      {lifecycle === 'inactive' && (
                        <span className="inline-flex rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        {(canEdit || canEditRoles) && (
                          <button
                            onClick={() => setEditTarget(a)}
                            className="inline-flex items-center gap-1 rounded-md border bg-card px-2 py-1 text-xs hover:bg-muted"
                            title="Edit display name + roles"
                          >
                            <Edit className="h-3 w-3" />
                            Edit
                          </button>
                        )}
                        {/* Pending only — resend the invite email. */}
                        {lifecycle === 'pending' && canInvite && (
                          <button
                            onClick={() => onResendInvite(a)}
                            disabled={rowBusy}
                            title="Resend the invite email to this admin"
                            className="inline-flex items-center gap-1 rounded-md border bg-card px-2 py-1 text-xs hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            <Send className="h-3 w-3" />
                            {rowBusy ? 'Resending…' : 'Resend invite'}
                          </button>
                        )}
                        {/* Inactive only — re-grant admin access. */}
                        {lifecycle === 'inactive' && canDeactivate && (
                          <button
                            onClick={() => onReactivate(a)}
                            disabled={rowBusy}
                            title="Restore admin access for this user"
                            className="inline-flex items-center gap-1 rounded-md border border-green-500 bg-card px-2 py-1 text-xs text-green-600 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-card"
                          >
                            <UserCheck className="h-3 w-3" />
                            {rowBusy ? 'Reactivating…' : 'Reactivate'}
                          </button>
                        )}
                        {/* Active + Pending — deactivate. */}
                        {canDeactivate && a.is_active && (
                          <button
                            onClick={() => setDeactivateTarget(a)}
                            disabled={deactivateDisabled}
                            title={deactivateReason || 'Deactivate this admin'}
                            className="inline-flex items-center gap-1 rounded-md border bg-card px-2 py-1 text-xs text-destructive hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-card"
                          >
                            <UserX className="h-3 w-3" />
                            Deactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showInvite && (
        <AdminInviteModal
          onClose={() => setShowInvite(false)}
          onSuccess={async () => {
            setShowInvite(false);
            await reload();
          }}
        />
      )}

      {editTarget && (
        <AdminEditModal
          admin={editTarget}
          onClose={() => setEditTarget(null)}
          onSuccess={async () => {
            setEditTarget(null);
            await reload();
          }}
        />
      )}

      {deactivateTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-card p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-destructive">
                  Deactivate admin
                </h3>
                <p className="text-sm text-muted-foreground">
                  {deactivateTarget.email}
                </p>
              </div>
            </div>
            <p className="mb-4 text-sm">
              They will lose admin access immediately. The Cognito user
              record is preserved so you can re-activate later if needed.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeactivateTarget(null)}
                disabled={submitting}
                className="rounded-md border bg-card px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={onDeactivate}
                disabled={submitting}
                className="rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
              >
                {submitting ? 'Deactivating…' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
